import { useMemo } from 'react';
import Slot from '@solvhealth/types/interfaces/Location/Slot';
import moment from 'moment-timezone';
import DateTime from '~/components/DateTime';

interface Props {
  slots: Slot[];
  timeZone: string;
}

interface DaypartSlots {
  morningSlots: Slot[];
  afternoonSlots: Slot[];
  eveningSlots: Slot[];
}

/**
 * This custom hook is used to divide an array of slots into chunks based on the part of the day
 * based on the timezone of the provided location.
 * Morning: 00:00 - 11:59
 * Afternoon: 12:00 - 16:59
 * Evening and after-hours: 17:00-24:00
 */
export default ({ timeZone, slots }: Props): DaypartSlots => {
  const firstAfternoonIndex = useMemo(() => {
    const temp = (slots ?? []).findIndex((slot) => {
      const slotDate = moment.tz(slot.appointmentDate, timeZone);
      return parseInt(DateTime.format(slotDate, timeZone)('HH') ?? '0', 10) >= 12;
    });
    return temp >= 0 ? temp : undefined;
  }, [timeZone, slots]);

  const firstEveningIndex = useMemo(() => {
    const temp = (slots ?? []).findIndex((slot) => {
      const slotDate = moment.tz(slot.appointmentDate, timeZone);
      return parseInt(DateTime.format(slotDate, timeZone)('HH') ?? '0', 10) >= 17;
    });
    return temp >= 0 ? temp : undefined;
  }, [timeZone, slots]);

  return useMemo<DaypartSlots>(() => {
    return {
      morningSlots: (slots ?? []).slice(0, firstAfternoonIndex ?? firstEveningIndex),
      afternoonSlots:
        firstAfternoonIndex !== undefined
          ? (slots ?? []).slice(firstAfternoonIndex, firstEveningIndex)
          : [],
      eveningSlots: firstEveningIndex !== undefined ? (slots ?? []).slice(firstEveningIndex) : [],
    };
  }, [firstAfternoonIndex, firstEveningIndex, slots]);
};
